import { FC, useCallback } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { TypeEnum } from "../../../types/answerTemplate";
import { useActions } from "../../../hooks/useActions";
import { useParams } from "react-router-dom";
import validatePhone from "../../../helpers/validatePhone";
import validateEmail from "../../../helpers/validateEmail";
import { Button, message } from "antd";
import getInvertedColorStyle from "../../../helpers/getInvertedColorStyle";
import { I18n } from "react-redux-i18n";
import validateIin from "../../../helpers/validateIin";
import { testValueUsingCustomRegExp } from "../../../helpers/testValueUsingCustomRegExp";

const OTHER_ANSWER_KEY = "other";

type ButtonsComponentProps = {
  loading: boolean;
  isAnswerMoved: boolean;
};

const ButtonsComponent: FC<ButtonsComponentProps> = ({ loading, isAnswerMoved }) => {
  const { instanceId } = useParams();
  const { answerQuestion } = useActions();
  const { question, answer, customAnswer } = useSelector((state: RootState) => state.questions);
  const maxOptionLimit = question?.answerTemplate?.maxOptionLimit;

  const isTemplate = useCallback(
    (type$: TypeEnum) => question?.answerTemplate?.type === type$,
    [question?.answerTemplate?.type],
  );

  const validateSpecialTemplate = useCallback(
    (type: TypeEnum) => {
      if (!question?.answerTemplate?.customAnswerIsRequired) {
        if (
          type === TypeEnum.LIMITED_RANGING &&
          answer?.csiAnswers?.[0]?.criterias?.some((criteria) => criteria?.score !== undefined)
        ) {
          return validateLimitedRanging();
        }
        return true;
      }

      if (type === TypeEnum.CSI) {
        return answer?.csiAnswers && answer.csiAnswers.length > 0
          ? answer.csiAnswers.every((csiAnswer) =>
              csiAnswer?.criterias?.every((criteria) => criteria?.score !== undefined),
            )
          : false;
      }

      if (type === TypeEnum.LIMITED_RANGING) {
        return validateLimitedRanging();
      }

      return !!answer?.answerValue;
    },
    [question, answer],
  );

  const validateLimitedRanging = useCallback(() => {
    if (!answer?.csiAnswers?.[0]?.criterias) {
      return false;
    }

    const criterias = answer.csiAnswers[0].criterias;
    const numberOfAssessments = question?.answerTemplate?.numberOfAssessments;
    const validScores = criterias.filter((criteria) => criteria?.score !== undefined);

    if (validScores.length === criterias.length) {
      return true;
    }

    if (numberOfAssessments !== undefined) {
      return validScores.length === numberOfAssessments;
    }

    const isAnyScoreSet = validScores.length > 0;
    const isRequired = question?.answerTemplate?.customAnswerIsRequired || isAnyScoreSet;

    return isRequired ? criterias.every((criteria) => criteria?.score !== undefined) : true;
  }, [question, answer]);

  const isAnswerValid = useCallback(() => {
    const isBasicTemplate = () =>
      isTemplate(TypeEnum.NPS) ||
      isTemplate(TypeEnum.EMAIL) ||
      isTemplate(TypeEnum.IIN) ||
      isTemplate(TypeEnum.NUMBER) ||
      isTemplate(TypeEnum.TEXT) ||
      isTemplate(TypeEnum.DATE) ||
      isTemplate(TypeEnum.RATING) ||
      isTemplate(TypeEnum.LOYALTY_INDEX) ||
      isTemplate(TypeEnum.DATETIME);

    const validateBasicTemplate = () => {
      if (!question?.answerTemplate?.customAnswerIsRequired && !answer?.answerValue) {
        return true;
      }
      if (!answer?.answerValue) {
        return false;
      }

      switch (true) {
        case isTemplate(TypeEnum.NUMBER):
          return validatePhone(answer.answerValue);
        case isTemplate(TypeEnum.IIN):
          return validateIin(answer.answerValue);
        case isTemplate(TypeEnum.EMAIL):
          return validateEmail(answer.answerValue);
        case isTemplate(TypeEnum.TEXT):
          if (question?.answerTemplate?.customRegexp) {
            return testValueUsingCustomRegExp({
              regexp: question?.answerTemplate?.customRegexp,
              value: answer?.answerValue,
            });
          }
          return true;
        default:
          return true;
      }
    };

    const validateChoiceTemplate = (type: TypeEnum) => {
      const choicesLength = answer?.choices?.length ?? 0;

      if (type === TypeEnum.MULTIPLE_CHOICE && maxOptionLimit) {
        if (choicesLength > 0) {
          return choicesLength === maxOptionLimit;
        }
        return !question?.answerTemplate?.customAnswerIsRequired;
      }

      if (type === TypeEnum.SINGLE_CHOICE) {
        if (!question?.answerTemplate?.customAnswerIsRequired) {
          return true;
        }
        return !!answer?.choices || !!answer?.answerValue;
      }

      if (type === TypeEnum.MULTIPLE_CHOICE) {
        if (!question?.answerTemplate?.customAnswerIsRequired) {
          return true;
        }
        return choicesLength > 0;
      }

      return false;
    };

    if (isBasicTemplate()) {
      return validateBasicTemplate();
    } else if (isTemplate(TypeEnum.SINGLE_CHOICE) || isTemplate(TypeEnum.MULTIPLE_CHOICE)) {
      return validateChoiceTemplate(
        isTemplate(TypeEnum.SINGLE_CHOICE) ? TypeEnum.SINGLE_CHOICE : TypeEnum.MULTIPLE_CHOICE,
      );
    } else if (
      isTemplate(TypeEnum.LOYALTY_INDEX) ||
      isTemplate(TypeEnum.RATING) ||
      isTemplate(TypeEnum.CSI) ||
      isTemplate(TypeEnum.LIMITED_RANGING)
    ) {
      return validateSpecialTemplate(
        isTemplate(TypeEnum.LOYALTY_INDEX)
          ? TypeEnum.LOYALTY_INDEX
          : isTemplate(TypeEnum.RATING)
          ? TypeEnum.RATING
          : isTemplate(TypeEnum.CSI)
          ? TypeEnum.CSI
          : TypeEnum.LIMITED_RANGING,
      );
    } else if (isTemplate(TypeEnum.RANGING)) {
      return !(!isAnswerMoved && question?.answerTemplate?.customAnswerIsRequired);
    } else if (
      !isTemplate(TypeEnum.START_BLOCK) &&
      !isTemplate(TypeEnum.END_BLOCK) &&
      !isTemplate(TypeEnum.INFO_BLOCK)
    ) {
      return !!answer?.answerValue || !!customAnswer;
    }
    return true;
  }, [question, answer, customAnswer, isTemplate, validateSpecialTemplate]);

  const handleAnswer = useCallback(() => {
    const answerValid = isAnswerValid();

    if (!answerValid) {
      if (answer?.choices && answer.choices.length > 0 && maxOptionLimit && isTemplate(TypeEnum.MULTIPLE_CHOICE)) {
        return message.error(I18n.t("maxOptionLimit", { limit: maxOptionLimit }));
      } else {
        return message.error(I18n.t("answerRequired"));
      }
    }

    if (instanceId && question?.questionId) {
      let answerObj = answer;

      if (isTemplate(TypeEnum.SINGLE_CHOICE) || isTemplate(TypeEnum.MULTIPLE_CHOICE)) {
        const choices = answer?.choices?.map((it) => String(it)) ?? [];
        answerObj = {
          csiAnswers: [],
          answerValue: customAnswer,
          choices: !customAnswer ? choices : [...choices, OTHER_ANSWER_KEY],
        };
      } else if (isTemplate(TypeEnum.IIN)) {
        answerObj = { ...answer, answerValue: answer?.answerValue?.replace(/\s/g, "") };
      }

      answerQuestion({
        instanceId,
        answer: answerObj,
        questionId: question.questionId,
      });
    }
  }, [instanceId, question, customAnswer, answer, isTemplate, isAnswerValid]);

  return (
    <div className={"btn-container"}>
      <Button type={"primary"} onClick={handleAnswer} loading={loading}>
        <span style={getInvertedColorStyle()}>{I18n.t("next")}</span>
      </Button>
    </div>
  );
};

export default ButtonsComponent;
